// Lets the user run the pipeline against a built-in anonymised KYC fixture.
// Avoids the need to upload real client data for demos and evaluation.
import { useState } from 'react';
import { SAMPLE_FIXTURES, loadFixture } from '../lib/fixtures.js';

const COLOR_STYLES = {
  red: 'border-red-900/40 bg-red-900/10 text-red-300',
  amber: 'border-amber-900/40 bg-amber-900/10 text-amber-200',
  emerald: 'border-emerald-900/40 bg-emerald-900/10 text-emerald-300',
};

export default function SamplePicker({ onLoaded, disabled = false }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);

  async function pick(id) {
    setError(null);
    setBusyId(id);
    try {
      const extracted = await loadFixture(id);
      onLoaded(extracted);
    } catch (err) {
      setError(err.message || 'Could not load fixture');
      setBusyId(null);
    }
  }

  return (
    <div className="rounded-lg border border-navy-700 bg-navy-800 p-5">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-gold">
          Try a sample
        </div>
        <div className="text-xs text-slate-400">No upload needed</div>
      </div>
      <p className="mb-4 text-xs text-slate-400">
        Three anonymised KYC files designed to exercise the pipeline at different difficulty levels.
        Click one to run a full review.
      </p>
      <ul className="space-y-2">
        {SAMPLE_FIXTURES.map((f) => {
          const isBusy = busyId === f.id;
          return (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => pick(f.id)}
                disabled={disabled || busyId !== null}
                className="block w-full rounded-md border border-navy-700 bg-navy-900 p-3 text-left transition hover:border-gold disabled:cursor-not-allowed disabled:opacity-50"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-white">{f.name}</div>
                    <div className="mt-0.5 text-xs text-slate-400">{f.summary}</div>
                  </div>
                  <span
                    className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-medium ${
                      COLOR_STYLES[f.expectedColor] || COLOR_STYLES.amber
                    }`}
                  >
                    {isBusy ? 'Loading…' : f.expected}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
      {error && (
        <div className="mt-3 rounded-md border border-red-900/50 bg-red-900/20 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}
